import { useState } from 'react';
import { Bell, Check, ExternalLink, AlertTriangle, Search, Info } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Notification, NotificationType } from '@/types/notification.types';
import { Link } from 'react-router-dom';
import { IconTile } from '@/components/ui/icon-tile';
import { EmptyState } from '@/components/ui/empty-state';
import type { Tone } from '@/lib/tones';

// Mock Data
const MOCK_NOTIFICATIONS: Notification[] = [
    {
        id: '1',
        title: 'Stolen Vehicle Match',
        message: 'VIN GH-8829-22 matches a reported stolen vehicle from Interpol database.',
        type: NotificationType.ALERT,
        timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 mins ago
        isRead: false,
        link: '/police/stolen/123'
    },
    {
        id: '2',
        title: 'External Search Result',
        message: 'Completed search for VIN JP-992-X on National Insurance Database.',
        type: NotificationType.SEARCH_RESULT,
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
        isRead: false,
        link: '/vin-lookup/JP-992-X'
    },
    {
        id: '3',
        title: 'System Maintenance',
        message: 'Scheduled maintenance tonight at 02:00 AM UTC.',
        type: NotificationType.SYSTEM,
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
        isRead: true
    }
];

export const NotificationDropdown = () => {
    const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS);
    const [isOpen, setIsOpen] = useState(false);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const markAsRead = (id: string) => {
        setNotifications(prev => prev.map(n =>
            n.id === id ? { ...n, isRead: true } : n
        ));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    };

    const typeStyle: Record<NotificationType, { Icon: typeof Info; tone: Tone }> = {
        [NotificationType.ALERT]: { Icon: AlertTriangle, tone: 'danger' },
        [NotificationType.SEARCH_RESULT]: { Icon: Search, tone: 'info' },
        [NotificationType.SYSTEM]: { Icon: Info, tone: 'neutral' },
    };

    const formatTime = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        return `${days}d ago`;
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:shadow-focus"
                    aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.75rem] font-bold leading-none text-primary-foreground ring-2 ring-card">
                            {unreadCount}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <p className="text-subheading">Notifications</p>
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            className="flex items-center gap-1 text-caption font-semibold text-primary hover:underline"
                        >
                            <Check className="h-3.5 w-3.5" /> Mark all read
                        </button>
                    )}
                </div>

                <div className="max-h-[24rem] overflow-y-auto p-1.5">
                    {notifications.length === 0 ? (
                        <EmptyState icon={Bell} title="You're all caught up" description="New alerts and search results will appear here." size="sm" />
                    ) : (
                        notifications.map((notification) => {
                            const { Icon, tone } = typeStyle[notification.type] ?? typeStyle[NotificationType.SYSTEM];
                            return (
                                <DropdownMenuItem
                                    key={notification.id}
                                    className={cn('cursor-pointer items-start gap-3 p-3', !notification.isRead && 'bg-primary-subtle/50')}
                                    onClick={() => markAsRead(notification.id)}
                                >
                                    <IconTile icon={Icon} tone={tone} size="sm" />
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className={cn('text-body-sm text-foreground', notification.isRead ? 'font-medium' : 'font-semibold')}>
                                                {notification.title}
                                            </p>
                                            <span className="whitespace-nowrap text-caption text-muted-foreground">
                                                {formatTime(notification.timestamp)}
                                            </span>
                                        </div>
                                        <p className="line-clamp-2 text-caption text-muted-foreground">{notification.message}</p>
                                        {notification.link && (
                                            <Link
                                                to={notification.link}
                                                className="inline-flex items-center gap-1 pt-0.5 text-caption font-semibold text-primary hover:underline"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                View details <ExternalLink className="h-3 w-3" />
                                            </Link>
                                        )}
                                    </div>
                                    {!notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
                                </DropdownMenuItem>
                            );
                        })
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
